import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  GifSettings as GifSettingsComponent,
  GifFrameSelector,
  GifPreview,
  useGifStore,
  GifFrame,
} from '@/features/gif';
import { GifTemplateSelector } from '@/features/gif/GifTemplateSelector';
import { TextOverlayEditor } from '@/features/gif/TextOverlayEditor';
import { CropEditor } from '@/features/gif/CropEditor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import { GifSessionSidebar } from '@/components/GifSessionSidebar';
import { useGifSessions } from '@/hooks/useGifSessions';
import { useStorageQuota } from '@/hooks/useStorageQuota';
import { ArrowLeft, FileImage, Home, Trash2, Film, Image as ImageIcon, Save, Folder } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface GifCreatorPageProps {
  embedded?: boolean;
}

export function GifCreatorPage({ embedded = false }: GifCreatorPageProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // GIF store (local state for current working session)
  const addFrames = useGifStore((state) => state.addFrames);
  const reset = useGifStore((state) => state.reset);
  const frames = useGifStore((state) => state.frames);
  const settings = useGifStore((state) => state.settings);
  const setFrames = useGifStore((state) => state.setFrames);
  const updateSettings = useGifStore((state) => state.updateSettings);
  
  // Sidebar / dialog state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Local sessions
  const {
    filteredSessions,
    isLoading: sessionsLoading,
    isSyncing,
    selectedSession,
    createSession,
    selectSession,
    renameSession,
    deleteSession,
    addFrameToSession,
    syncSession,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
  } = useGifSessions();
  
  // Storage quota
  const { quota } = useStorageQuota();
  const storageInfo = {
    used: quota?.used || 0,
    quota: quota?.quota || 0,
    percentUsed: quota?.percentUsed || 0,
  };

  // Handle frames passed via URL (from frame gallery/extractor)
  useEffect(() => {
    const framesParam = searchParams.get('frames');
    const sessionId = searchParams.get('session');
    const mode = searchParams.get('mode') || 'replace'; // 'add' or 'replace'
    
    if (framesParam) {
      try {
        // Parse the frames data from URL
        const decoded = atob(framesParam);
        const frameData: Array<{ id: string; imageData: string; width: number; height: number }> = JSON.parse(decoded);
        
        if (Array.isArray(frameData) && frameData.length > 0) {
          // Convert to GifFrame format
          const gifFrames: Omit<GifFrame, 'order'>[] = frameData.map((frame) => ({
            id: `gif-${frame.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            sourceFrameId: frame.id,
            imageData: frame.imageData,
            width: frame.width || 640,
            height: frame.height || 360,
          }));
          
          if (sessionId) {
            (async () => {
              for (const frame of gifFrames) {
                await addFrameToSession(sessionId, {
                  sourceFrameId: frame.sourceFrameId,
                  imageData: frame.imageData,
                  width: frame.width,
                  height: frame.height,
                });
              }
              // Select the session
              selectSession(sessionId);
            })();
          } else {
            // Add to local store
            if (mode === 'replace') {
              reset();
            }
            addFrames(gifFrames);
          }
        }
      } catch (error) {
        console.error('Failed to parse frames from URL:', error);
      }
      
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('frames');
      nextParams.delete('mode');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, addFrames, reset, addFrameToSession, selectSession]);

  // Load selected session's frames into local store
  useEffect(() => {
    if (selectedSession) {
      // Convert cloud session frames to local GifFrame format
      const localFrames: GifFrame[] = selectedSession.frames.map((f) => ({
        id: f.id,
        sourceFrameId: f.sourceFrameId,
        imageData: f.url || f.blobUrl, // Use signed URL if available
        order: f.order,
        duration: f.duration,
        width: f.width,
        height: f.height,
      }));
      setFrames(localFrames);
      
      // Apply session settings
      updateSettings({
        playbackMode: selectedSession.settings.playbackMode as 'loop' | 'bounce' | 'one-shot',
        frameRate: selectedSession.settings.frameRate,
        quality: selectedSession.settings.quality as 'low' | 'medium' | 'high' | 'custom',
        maxWidth: selectedSession.settings.maxWidth,
        loop: selectedSession.settings.loop,
        defaultFrameDuration: selectedSession.settings.defaultFrameDuration,
      });
    }
  }, [selectedSession, setFrames, updateSettings]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleAddFromExtractor = () => {
    // Pass session ID if one is selected
    const params = new URLSearchParams({ view: 'studio' });
    if (selectedSession) params.set('returnSession', selectedSession.id);
    navigate(`/?${params.toString()}`);
  };

  const handleAddFromLibrary = () => {
    const params = new URLSearchParams({ view: 'library', library: 'frames' });
    if (selectedSession) params.set('returnSession', selectedSession.id);
    navigate(`/?${params.toString()}`);
  };

  const handleClearAll = () => {
    reset();
    if (selectedSession) {
      selectSession(null);
    }
  };

  const handleSelectSession = useCallback((session: { id: string }) => {
    selectSession(session.id);
  }, [selectSession]);

  const handleCreateSession = useCallback(async (name: string) => {
    const session = await createSession(name, settings);
    return session;
  }, [createSession, settings]);

  // Save current frames to local session (active session or create new)
  const handleSaveSession = async () => {
    if (frames.length === 0) {
      toast.error('No frames to save');
      return;
    }

    // Prepare frames data for sync
    const framesToSync = frames.map((frame, index) => ({
      id: frame.id,
      sourceFrameId: frame.sourceFrameId,
      imageData: frame.imageData,
      order: frame.order ?? index,
      width: frame.width,
      height: frame.height,
      duration: frame.duration,
    }));

    if (selectedSession) {
      // Save to active session using batch sync
      const updated = await syncSession(selectedSession.id, framesToSync, settings);
      if (updated) {
        toast.success('Session saved');
      }
    } else {
      // Create new session and sync frames to it
      const sessionName = `GIF Session ${new Date().toLocaleString()}`;
      const session = await createSession(sessionName, settings);
      
      if (session) {
        const updated = await syncSession(session.id, framesToSync, settings);
        if (updated) {
          selectSession(session.id);
          toast.success('Session created and saved');
        }
      }
    }
  };

  return (
    <div className={cn('flex bg-background', embedded ? 'min-h-0' : 'min-h-screen')}>
      {/* Sidebar */}
      <GifSessionSidebar
        sessions={filteredSessions}
        isLoading={sessionsLoading}
        currentSessionId={selectedSession?.id || null}
        storageInfo={storageInfo}
        sortBy={sortBy}
        onSortChange={setSortBy}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSelectSession={handleSelectSession}
        onCreateSession={handleCreateSession}
        onRenameSession={renameSession}
        onDeleteSession={deleteSession}
        isOpen={sidebarOpen}
        onOpenChange={setSidebarOpen}
      />
      
      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {!embedded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGoBack}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              )}
              <div className="flex items-center gap-2">
                <FileImage className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold">GIF Creator</h1>
                {frames.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {frames.length} frames
                  </Badge>
                )}
              </div>
              {selectedSession && (
                <Badge variant="outline" className="gap-1">
                  <Folder className="h-3 w-3" />
                  {selectedSession.name}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!sidebarOpen && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSidebarOpen(true)}
                  className="gap-2 md:hidden"
                >
                  <Folder className="h-4 w-4" />
                  Sessions
                </Button>
              )}
              
              {/* Add frames buttons */}
              <Button variant="outline" size="sm" onClick={handleAddFromExtractor}>
                <Film className="h-4 w-4 mr-2" />
                Add from Video
              </Button>
              <Button variant="outline" size="sm" onClick={handleAddFromLibrary}>
                <ImageIcon className="h-4 w-4 mr-2" />
                Add from Library
              </Button>
              
              {/* Save session button - shows for both new and existing sessions */}
              {frames.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveSession}
                  disabled={isSyncing}
                  className="gap-2"
                >
                  {isSyncing ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {selectedSession ? 'Save' : 'Save Session'}
                    </>
                  )}
                </Button>
              )}
              
              {/* Clear all */}
              {frames.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearAll}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              )}
              
              {!embedded && (
                <Button variant="outline" size="sm" onClick={handleGoHome}>
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
              )}
            </div>
          </div>

          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column: Tabs (Settings, Templates, Text, Crop) and Preview */}
            <div className="lg:col-span-1 space-y-6">
              <Tabs defaultValue="settings" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="templates">Presets</TabsTrigger>
                  <TabsTrigger value="text">Text</TabsTrigger>
                  <TabsTrigger value="crop">Crop</TabsTrigger>
                </TabsList>
                <TabsContent value="settings" className="mt-3">
                  <GifSettingsComponent />
                </TabsContent>
                <TabsContent value="templates" className="mt-3">
                  <GifTemplateSelector />
                </TabsContent>
                <TabsContent value="text" className="mt-3">
                  <TextOverlayEditor />
                </TabsContent>
                <TabsContent value="crop" className="mt-3">
                  <CropEditor />
                </TabsContent>
              </Tabs>
              <GifPreview />
            </div>

            {/* Right column: Frame Selector */}
            <div className="lg:col-span-2">
              <GifFrameSelector />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GifCreatorPage;
